import React, { useEffect } from 'react';
import { useLocation } from '@reach/router';
import { ThemeProvider } from '../contexts/ThemeContext';
import DarkModeBG from '../components/DarkModeBG';
import Background from '../components/Background';
import Navbar from '../components/Navbar';
import Settings from '../components/Settings';
import { Helmet } from 'react-helmet';
import config from '../../config';
import Disclaimer from '../components/Disclaimer';
import LoaderScreen from '../components/Other/LoaderScreen';
import { ErrorProvider } from '../contexts/ErrorContext';
import ErrorNotification from '../components/Other/ErrorNotification';
import MobileOverlay from '../components/MobileOverlay';
import useDeviceTypeAndOrientation from '../utils/useDeviceTypeAndOrientation';
import { PostHogProvider } from 'posthog-js/react'

const normalizePath = (path) => {
    if (path === "/") return path;
    return path.replace(/\/$/, "");
};


const SiteCore = ({ children }) => {

    const { platform, orientation, isSupported } = useDeviceTypeAndOrientation();

    const [siteTitle, setSiteTitle] = React.useState('Eigen X Portal');
    const [isMounted, setIsMounted] = React.useState(false);
    const [displayContent, setDisplayContent] = React.useState(false);
    const [timeoutDone, setTimeoutDone] = React.useState(false);
    const [videoReady, setVideoReady] = React.useState(false);
    // const [force, setForce] = React.

    const location = useLocation();

    useEffect(() => {
        let titlePrefix = '';
        const normalizedPath = normalizePath(location.pathname);

        if (normalizedPath === '/contact-us') {
            titlePrefix = 'Contact Us';
        } else {
            const navItem = config.navigation.find(
                item => item.path === normalizedPath
            );
            if (navItem) {
                titlePrefix = navItem.name;
            }
        }

        const title = titlePrefix
            ? `${titlePrefix} | Eigen X Portal`
            : 'Eigen X Portal';

        setSiteTitle(title);
    }, [location.pathname]);



    const onVideoReady = () => {
        setVideoReady(true);
    };

    // 1) On mount, set a timer to flip `timeoutDone` after 1.8s
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setTimeoutDone(true);
        }, 1800);

        // Cleanup if this component unmounts before 1.8s
        return () => clearTimeout(timer);
    }, []);

    // 2) Whenever `videoReady` or `timeoutDone` changes, check if both are true
    React.useEffect(() => {
        if (videoReady && timeoutDone) {
            // Display all content and fade out the loader screen
            setIsMounted(true);

            // A small delay before showing the content
            // const handle = setTimeout(() => {
            setDisplayContent(true);
            // }, 500);

            // return () => clearTimeout(handle);
        }
    }, [videoReady, timeoutDone]);

    const phOptions = {
        api_host: process.env.GATSBY_POSTHOG_HOST,
        defaults: '2025-05-24',
        person_profiles: 'always',
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking
        autocapture: false,
        enable_heatmaps: true
    }

    return (
        <PostHogProvider apiKey={process.env.GATSBY_POSTHOG_API_KEY} options={phOptions}>
            <ThemeProvider>
                <ErrorProvider>
                    <Helmet>
                        <title>{siteTitle}</title>
                        <meta charSet="utf-8" />
                        <meta name="description" content={config.meta.description} />
                        <meta name="keywords" content={config.meta.keywords.join(', ')} />
                        <link rel="author" href="/humans.txt" />
                        <link rel="canonical" href={config.meta.canonical} />

                        <meta property="og:title" content={siteTitle} />
                        <meta property="og:description" content={config.meta.description} />
                        <meta property="og:type" content="website" />
                        <meta
                            property="og:url"
                            content={`${config.meta.canonical}${location}`}
                        />
                        <meta property="og:site_name" content={config.meta.title} />
                        <meta property="og:see_also" content={config.meta.see_also} />

                        <meta name="twitter:card" content="summary" />
                        <meta
                            name="twitter:url"
                            content={`${config.meta.canonical}${location}`}
                        />
                        <meta name="twitter:title" content={siteTitle} />
                        <meta name="twitter:description" content={config.meta.description} />
                    </Helmet>

                    {!isSupported && (
                        <MobileOverlay platform={platform} orientation={orientation} />
                    )}

                    {/* Pass in onVideoReady so that we can set `videoReady` once it’s done */}
                    <DarkModeBG onReady={onVideoReady} />

                    <main>

                        <ErrorNotification />

                        {/* Loader screen fades out when `isMounted` is true (after videoReady + 1.8s) */}
                        <LoaderScreen fadeOut={isMounted} fadeOutDelay={500} />

                        {/* Display the background & children once `displayContent` is true */}
                        {displayContent && (
                            <Background navbar={<Navbar />} showBorder={!videoReady}>
                                {children}
                                <Settings />
                            </Background>
                        )}

                        <Disclaimer />
                    </main>
                </ErrorProvider>
            </ThemeProvider>
        </PostHogProvider>
    );
};

export default SiteCore;
